/**
 * GroupOrderWebSocketService
 *
 * Manages the WebSocket connection for group orders.
 *
 * Lifecycle:
 *   - Call connect() after createGroup or joinGroup succeeds.
 *   - Call disconnect() when navigating away, GROUP_PAID, or PARTICIPANT_REMOVED (own cart).
 *   - On unintentional close → exponential backoff reconnect (up to 5 attempts).
 *   - While disconnected, polls fetchGroupState every POLL_INTERVAL_MS.
 *
 * All events trigger fetchGroupState via the store dispatch. Events that require
 * immediate UI action (PARTICIPANT_REMOVED, GROUP_PAID, COUNTDOWN_LOCKED) also
 * call the relevant handler callbacks.
 *
 * WS URL: wss://{host}/owa/group-orders/ws/{inviteUuid}?participantCartOrderId={id}
 *
 * Event types: GROUP_CHANGED, CART_CHANGED, PARTICIPANT_REMOVED, CHECKOUT_CHANGED,
 *              GROUP_PAID, COUNTDOWN_LOCKED
 *
 * Stale-read handling:
 *   The backend sends the WS event before the HTTP GET endpoint reflects the change.
 *   For version-bearing events, fetchWithVersionGuard retries until the fetched
 *   group.version >= the expected version, with exponential backoff up to ~10s total.
 */

import Vue from 'vue'

var MAX_RECONNECT_ATTEMPTS = 5
var BASE_RECONNECT_DELAY_MS = 1000
var MAX_RECONNECT_DELAY_MS = 30000
var POLL_INTERVAL_MS = 5000

// Retry config for stale-read guard
var FETCH_RETRY_DELAYS_MS = [300, 600, 1200, 2400, 4800] // ~9s total max

// Coalescing window for OTHER participants' CART_CHANGED events on the host's connection
// (DEV-11114). This is a direct floor on how quickly the host sees a guest's change, so
// it's deliberately shorter than shared-cart-ws.service.js's 500ms: the expensive case is
// a BURST (several guests editing at once), and 250ms still collapses those, while a
// single isolated change stays close to real-time. The guard's own 300ms initial delay is
// skipped on this path (see _debouncedHostFetch) so this is the only added wait.
var HOST_CART_DEBOUNCE_MS = 250

export function GroupOrderWebSocketService(options) {
  this.inviteUuid = options.inviteUuid
  this.participantCartOrderId = options.participantCartOrderId
  this.store = options.store
  this.router = options.router
  this.handlers = options.handlers || {}

  this.ws = null
  this.reconnectAttempts = 0
  this._intentionalClose = false
  this._reconnectTimer = null
  this._pollTimer = null
  this._retryTimers = []
  this._hostCartDebounceTimer = null
  this._pendingHostVersion = null
  // Set while the host is bulk-removing unready carts right before checkout (DEV-10330) --
  // one "X left the group" snack per removed cart would otherwise stomp the checkout
  // error/success snack shown right after, since the snackbar only holds one message.
  this.suppressLeftNotifications = false
}

GroupOrderWebSocketService.prototype.connect = function() {
  if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
    return
  }

  var wsBase = this._buildWsBase()
  if (!wsBase) {
    console.warn('[GroupOrderWS] Cannot determine WS base URL — skipping connect')
    return
  }

  var url = wsBase + '/group-orders/ws/' + this.inviteUuid + '?participantCartOrderId=' + this.participantCartOrderId
  var self = this

  try {
    this.ws = new WebSocket(url)
  } catch (e) {
    console.error('[GroupOrderWS] Failed to create WebSocket:', e)
    this._scheduleReconnect()
    return
  }

  this.ws.onopen = function() {
    self.reconnectAttempts = 0
    self._stopPolling()
    self.store.commit('groupOrder/SET_WS_CONNECTED', true)
  }

  this.ws.onmessage = function(event) {
    var data
    try {
      data = JSON.parse(event.data)
    } catch (e) {
      console.warn('[GroupOrderWS] Non-JSON message received:', event.data)
      return
    }
    self._handleEvent(data)
  }

  this.ws.onclose = function() {
    self.store.commit('groupOrder/SET_WS_CONNECTED', false)
    if (!self._intentionalClose) {
      self._startPolling()
      self._scheduleReconnect()
    }
  }

  this.ws.onerror = function(event) {
    console.error('[GroupOrderWS] WebSocket error:', event)
    if (self.handlers.onError) self.handlers.onError(event)
  }
}

GroupOrderWebSocketService.prototype.disconnect = function() {
  this._intentionalClose = true
  this._stopPolling()
  this._clearReconnectTimer()
  this._clearRetryTimers()
  this._clearHostCartDebounceTimer()
  if (this.ws) {
    this.ws.close()
    this.ws = null
  }
  this.store.commit('groupOrder/SET_WS_CONNECTED', false)
}

GroupOrderWebSocketService.prototype._handleEvent = function(data) {
  var eventType = data.eventType
  var cartOrderId = data.cartOrderId
  var groupVersion = data.groupVersion

  if (groupVersion) {
    this.store.commit('groupOrder/SET_GROUP_VERSION', groupVersion)
  }

  var self = this
  var myId = this.store.getters['groupOrder/myParticipantCartOrderId']
  var isHost = this.store.state.groupOrder && this.store.state.groupOrder.isHost

  if (eventType === 'GROUP_CHANGED') {
    // Backend sends WS event before the GET endpoint reflects the change.
    // Retry with backoff until group.version >= event version.
    self._fetchWithVersionGuard(groupVersion)

  } else if (eventType === 'CART_CHANGED') {
    if (cartOrderId === myId) {
      // Own cart: fetch immediately, the user is waiting on their own edit.
      self._fetchWithVersionGuard(groupVersion)
    } else if (isHost) {
      // Someone ELSE's cart, and we're the host -- the host receives one of these for
      // every guest's every edit, and a table of guests all adding items produces a
      // continuous stream. Each fetch rebuilds group state and re-renders the host's
      // whole member list, so firing one per event is what made the host's UI crawl
      // (DEV-11114). Coalesce bursts into a single fetch; the same debounce the shared
      // cart's WS service already uses for its equivalent broadcast (CART_DEBOUNCE_MS
      // in shared-cart-ws.service.js). The highest version seen during the burst is
      // carried into that one fetch, so the stale-read guard still retries until the
      // GET reflects the newest change -- coalescing the requests, not weakening them.
      self._debouncedHostFetch(groupVersion)
    }

  } else if (eventType === 'PARTICIPANT_REMOVED') {
    if (cartOrderId === myId) {
      self.disconnect()
      self.store.dispatch('groupOrder/handleRemoved')
      if (self.handlers.onRemoved) self.handlers.onRemoved()
    } else if (self.suppressLeftNotifications) {
      self._fetchWithVersionGuard(groupVersion)
    } else {
      // Capture the leaving participant's name before fetchGroupState clears it
      var carts = (self.store.state.groupOrder && self.store.state.groupOrder.participantCarts) || []
      var leaving = null
      for (var i = 0; i < carts.length; i++) {
        if (carts[i].orderId === cartOrderId) { leaving = carts[i]; break }
      }
      var leavingName = leaving ? leaving.guestName : 'A participant'
      self._fetchWithVersionGuard(groupVersion).then(function() {
        self.store.dispatch('showSnack', {
          text: Vue.prototype.$vuetify.t('$vuetify.groupOrder.snackParticipantLeftHost', [leavingName]),
          color: 'primary',
          timeout: 4000,
        }, { root: true })
      })
    }

  } else if (eventType === 'CHECKOUT_CHANGED') {
    if (!isHost) {
      self.store.commit('groupOrder/SET_CART_LOCKED', { locked: true, reason: 'CHECKOUT' })
    }
    self._fetchWithVersionGuard(groupVersion)

  } else if (eventType === 'GROUP_PAID') {
    self.disconnect()
    self.store.commit('groupOrder/SET_CHECKOUT_STATUS', 'paid')
    if (self.handlers.onGroupPaid) self.handlers.onGroupPaid()

  } else if (eventType === 'COUNTDOWN_LOCKED') {
    self.store.commit('groupOrder/SET_CART_LOCKED', true)
    self._fetchWithVersionGuard(groupVersion)
    if (self.handlers.onCountdownLocked) self.handlers.onCountdownLocked()

  } else {
    console.warn('[GroupOrderWS] Unknown event type:', eventType)
  }
}

/**
 * Coalesce a burst of other participants' CART_CHANGED events (host only) into one
 * fetch. See the CART_CHANGED branch in _handleEvent for why.
 */
GroupOrderWebSocketService.prototype._debouncedHostFetch = function(groupVersion) {
  var self = this
  // Keep the highest version from the coalesced burst so the single fetch that follows
  // still waits for a GET that reflects the newest change (see _fetchWithVersionGuard).
  if (groupVersion && (!this._pendingHostVersion || groupVersion > this._pendingHostVersion)) {
    this._pendingHostVersion = groupVersion
  }
  // Only the timer is reset here, not _pendingHostVersion -- restarting the debounce
  // window must keep the version accumulated so far (that's the point of coalescing).
  if (this._hostCartDebounceTimer) clearTimeout(this._hostCartDebounceTimer)
  this._hostCartDebounceTimer = setTimeout(function() {
    self._hostCartDebounceTimer = null
    var version = self._pendingHostVersion
    self._pendingHostVersion = null
    if (self._intentionalClose) return
    // skipInitialDelay: the debounce window above (500ms) is already a longer backend
    // propagation buffer than the guard's own 300ms one.
    self._fetchWithVersionGuard(version, true)
  }, HOST_CART_DEBOUNCE_MS)
}

GroupOrderWebSocketService.prototype._clearHostCartDebounceTimer = function() {
  if (this._hostCartDebounceTimer) {
    clearTimeout(this._hostCartDebounceTimer)
    this._hostCartDebounceTimer = null
  }
  this._pendingHostVersion = null
}

/**
 * Fetch group state, retrying with backoff until the response reflects
 * the expected version. If expectedVersion is absent, fetches once with
 * an initial 300ms delay (backend write propagation buffer).
 * Returns a Promise that resolves when a fresh-enough response is received.
 */
GroupOrderWebSocketService.prototype._fetchWithVersionGuard = function(expectedVersion, skipInitialDelay) {
  var self = this
  var delays = FETCH_RETRY_DELAYS_MS.slice() // copy so we can shift

  function attempt() {
    if (self._intentionalClose) return Promise.resolve()
    return self.store.dispatch('groupOrder/fetchGroupState').then(function(responseData) {
      var fetchedVersion = responseData && responseData.group && responseData.group.version
      var isFresh = !expectedVersion || !fetchedVersion || fetchedVersion >= expectedVersion
      if (!isFresh && delays.length > 0) {
        var delay = delays.shift()
        return new Promise(function(resolve) {
          var timer = setTimeout(function() {
            self._removeRetryTimer(timer)
            resolve(attempt())
          }, delay)
          self._retryTimers.push(timer)
        })
      }
      return responseData
    }).catch(function() {
      // Network error during retry — stop, polling will catch up
    })
  }

  // Initial delay: give backend time to commit before first read. Skipped when the
  // caller already waited longer than this (the host's 500ms debounce) -- otherwise
  // that path pays the propagation buffer twice, adding 300ms of latency to every
  // guest change the host sees for no benefit. Retries below are unaffected.
  if (skipInitialDelay) return attempt()
  return new Promise(function(resolve) {
    var timer = setTimeout(function() {
      self._removeRetryTimer(timer)
      resolve(attempt())
    }, FETCH_RETRY_DELAYS_MS[0])
    self._retryTimers.push(timer)
  })
}

GroupOrderWebSocketService.prototype._clearRetryTimers = function() {
  for (var i = 0; i < this._retryTimers.length; i++) {
    clearTimeout(this._retryTimers[i])
  }
  this._retryTimers = []
}

GroupOrderWebSocketService.prototype._removeRetryTimer = function(timer) {
  var idx = this._retryTimers.indexOf(timer)
  if (idx !== -1) this._retryTimers.splice(idx, 1)
}

GroupOrderWebSocketService.prototype._scheduleReconnect = function() {
  if (this._intentionalClose) return
  if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('[GroupOrderWS] Max reconnect attempts reached. Staying in poll-only mode.')
    return
  }

  var delay = Math.min(
    BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
    MAX_RECONNECT_DELAY_MS
  )
  var self = this
  this._clearReconnectTimer()
  this._reconnectTimer = setTimeout(function() {
    self.reconnectAttempts++
    self.connect()
  }, delay)
}

GroupOrderWebSocketService.prototype._clearReconnectTimer = function() {
  if (this._reconnectTimer) {
    clearTimeout(this._reconnectTimer)
    this._reconnectTimer = null
  }
}

GroupOrderWebSocketService.prototype._startPolling = function() {
  if (this._pollTimer) return
  var self = this
  this._pollTimer = setInterval(function() {
    if (self._intentionalClose) return
    self.store.dispatch('groupOrder/fetchGroupState').catch(function() {})
  }, POLL_INTERVAL_MS)
}

GroupOrderWebSocketService.prototype._stopPolling = function() {
  if (this._pollTimer) {
    clearInterval(this._pollTimer)
    this._pollTimer = null
  }
}

GroupOrderWebSocketService.prototype._buildWsBase = function() {
  var apiBase = process.env.VUE_APP_API || ''
  if (apiBase) {
    return apiBase.replace(/^http/, 'ws').replace(/\/+$/, '')
  }
  if (typeof window !== 'undefined') {
    var protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return protocol + '//' + window.location.host
  }
  return null
}
